import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Search, MapPin, X, PackagePlus } from "lucide-react";

/* ---------- helpers ---------- */
const DEG = Math.PI / 180;
const hueOf = (s) => { let h = 0; for (const ch of s) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return h % 360; };
const canSize = (qty) => (qty >= 10 ? [0.09, 0.32] : qty >= 2 ? [0.06, 0.22] : [0.035, 0.14]);

/* ---------- 3D pieces ---------- */
function Room({ layout }) {
  const { w = 5, d = 6, h = 2.8, gate } = layout || {};
  const wallMat = { color: "#e8e4d8", transparent: true, opacity: 0.35 };
  const seg = (x, z, sx, sz) => (
    <mesh position={[x, h / 2, z]}>
      <boxGeometry args={[sx, h, sz]} />
      <meshStandardMaterial {...wallMat} />
    </mesh>
  );
  const g = gate || { wall: "right", z0: d * 0.3, z1: d * 0.47 };
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[w / 2, 0, d / 2]} receiveShadow>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial color="#d9d4c7" />
      </mesh>
      <gridHelper args={[Math.max(w, d), Math.max(w, d), "#b8b2a4", "#cfc9bb"]} position={[w / 2, 0.002, d / 2]} />
      {seg(w / 2, 0, w, 0.08)}            {/* back wall */}
      {seg(0, d / 2, 0.08, d)}            {/* left wall */}
      {seg(w, g.z0 / 2, 0.08, g.z0)}      {/* right wall before gate */}
      {seg(w, (g.z1 + d) / 2, 0.08, d - g.z1)} {/* right wall after gate */}
      <mesh position={[w, 0.01, (g.z0 + g.z1) / 2]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[0.6, g.z1 - g.z0]} />
        <meshStandardMaterial color="#c8a24a" />
      </mesh>
      <Html position={[w + 0.3, 0.9, (g.z0 + g.z1) / 2]} center distanceFactor={6} zIndexRange={[5, 0]}>
        <div className="px-2 py-0.5 rounded bg-yellow-600 text-white text-xs font-bold shadow">GATE</div>
      </Html>
    </group>
  );
}

function Rack({ rack, cans, selectedShelf, matches, hovered, onSelectShelf, onHover }) {
  const L = rack.layout || {};
  const { x = 0, z = 0, w = 0.9, d = 0.45, h = 2.2, rot = 0, rows = 5 } = L;
  const sh = h / rows;
  const shelves = rack.shelves || [];
  const post = (px, pz) => (
    <mesh key={`${px}${pz}`} position={[px, h / 2, pz]}>
      <boxGeometry args={[0.04, h, 0.04]} />
      <meshStandardMaterial color="#4a4f57" />
    </mesh>
  );
  return (
    <group position={[x, 0, z]} rotation={[0, rot * DEG, 0]}>
      {post(-w / 2, -d / 2)}{post(w / 2, -d / 2)}{post(-w / 2, d / 2)}{post(w / 2, d / 2)}
      <Html position={[0, h + 0.18, 0]} center distanceFactor={6} zIndexRange={[5, 0]}>
        <div className="px-2 py-0.5 rounded bg-slate-800 text-white text-xs font-bold shadow whitespace-nowrap">
          {rack.name.replace("Rack ", "R")}
        </div>
      </Html>
      {shelves.map((s) => {
        const row = (s.layout || {}).row || 1;
        const y = (row - 1) * sh + 0.02;
        const list = cans[s.id] || [];
        const hit = list.some((c) => matches.has(c.id));
        const sel = selectedShelf === s.id;
        return (
          <group key={s.id}>
            <mesh position={[0, y, 0]} onClick={(e) => { e.stopPropagation(); onSelectShelf(s.id); }}
                  onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; }}
                  onPointerOut={() => { document.body.style.cursor = "default"; }}>
              <boxGeometry args={[w, 0.03, d]} />
              <meshStandardMaterial color={sel ? "#d6b156" : hit ? "#3fae6a" : "#8f949c"}
                                    emissive={sel ? "#8a6a1a" : hit ? "#1f6b3c" : "#000"} emissiveIntensity={0.6} />
            </mesh>
            {list.map((c, i) => {
              const [r, ch] = canSize(c.current_qty);
              const hh = Math.min(ch, sh * 0.8);
              const px = -w / 2 + (w / (list.length + 1)) * (i + 1);
              const m = matches.has(c.id);
              const hv = hovered === c.id;
              return (
                <mesh key={c.id} position={[px, y + 0.015 + hh / 2, 0]}
                      scale={m || hv ? 1.25 : 1}
                      onClick={(e) => { e.stopPropagation(); onSelectShelf(s.id, c.id); }}
                      onPointerOver={(e) => { e.stopPropagation(); onHover(c.id); document.body.style.cursor = "pointer"; }}
                      onPointerOut={() => { onHover(null); document.body.style.cursor = "default"; }}>
                  <cylinderGeometry args={[r, r, hh, 14]} />
                  <meshStandardMaterial color={`hsl(${hueOf(c.item_id)}, 55%, ${m ? 55 : 68}%)`}
                                        emissive={m ? "#c8a24a" : "#000"} emissiveIntensity={m ? 0.9 : 0} />
                  {hv && (
                    <Html position={[0, hh / 2 + 0.12, 0]} center distanceFactor={6}>
                      <div className="px-2 py-1 rounded bg-white border border-border text-xs shadow whitespace-nowrap">
                        <b>{c.item_name}</b> · {c.current_qty} {c.base_unit}
                        <div className="font-mono text-[10px] text-muted-foreground">{c.code}</div>
                      </div>
                    </Html>
                  )}
                </mesh>
              );
            })}
          </group>
        );
      })}
    </group>
  );
}

function CameraRig({ focus, room }) {
  const { camera } = useThree();
  const controls = useRef();
  useEffect(() => {
    if (!controls.current) return;
    const { w = 5, d = 6 } = room || {};
    if (focus) {
      controls.current.target.set(focus.x, focus.y, focus.z);
      camera.position.set(focus.x + focus.dx * 2.2, focus.y + 1.2, focus.z + focus.dz * 2.2);
    } else {
      controls.current.target.set(w / 2, 0.8, d / 2);
      camera.position.set(w / 2 + 1, 5.5, d + 5.5);
    }
    controls.current.update();
  }, [focus, room, camera]);
  return <OrbitControls ref={controls} makeDefault enableDamping maxPolarAngle={Math.PI / 2.05} minDistance={1} maxDistance={20} />;
}

/* ---------- page ---------- */
export default function RoomMap() {
  const qc = useQueryClient();
  const { data: rooms = [] } = useQuery({
    queryKey: ["map-rooms"], queryFn: async () => (await api.get("/map/rooms")).data,
  });
  const [params] = useSearchParams();
  const [roomId, setRoomId] = useState(params.get("room") || "");
  useEffect(() => { if (!roomId && rooms.length) setRoomId(rooms[0].id); }, [rooms, roomId]);
  const { data, refetch } = useQuery({
    queryKey: ["map-room", roomId], enabled: !!roomId,
    queryFn: async () => (await api.get(`/map/rooms/${roomId}`)).data,
  });
  const [q, setQ] = useState(params.get("q") || "");
  const [selected, setSelected] = useState(null);  // shelf id
  const [focusCan, setFocusCan] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [focus, setFocus] = useState(null);
  const [putMode, setPutMode] = useState(false);
  const [putQ, setPutQ] = useState("");

  const racks = data?.racks || [];
  const containers = data?.containers || [];
  const shelfById = useMemo(() => {
    const m = {};
    racks.forEach((r) => (r.shelves || []).forEach((s) => (m[s.id] = { ...s, rack: r })));
    return m;
  }, [racks]);
  const cansByShelf = useMemo(() => {
    const m = {};
    containers.forEach((c) => { (m[c.location_id] ||= []).push(c); });
    Object.values(m).forEach((l) => l.sort((a, b) => a.code.localeCompare(b.code)));
    return m;
  }, [containers]);
  const unplaced = containers.filter((c) => !shelfById[c.location_id]);
  const query = q.trim().toLowerCase();
  const matches = useMemo(() => new Set(query ? containers.filter((c) => c.search.includes(query)).map((c) => c.id) : []), [containers, query]);
  const results = query ? containers.filter((c) => matches.has(c.id)) : [];

  const rackWorld = (rack) => {
    const L = rack.layout || {};
    const dx = Math.sin((L.rot || 0) * DEG), dz = Math.cos((L.rot || 0) * DEG);
    return { x: L.x, z: L.z, dx, dz };
  };
  const showShelf = (shelfId, canId = null) => {
    setSelected(shelfId); setFocusCan(canId); setPutMode(false);
    const s = shelfById[shelfId];
    if (s) {
      const L = s.rack.layout || {};
      const row = (s.layout || {}).row || 1;
      const { x, z, dx, dz } = rackWorld(s.rack);
      setFocus({ x, y: (row - 0.5) * ((L.h || 2.2) / (L.rows || 5)), z, dx, dz });
    }
  };

  const deepShelf = params.get("shelf");
  const jumped = useRef(false);
  useEffect(() => {
    if (!jumped.current && deepShelf && shelfById[deepShelf]) { jumped.current = true; showShelf(deepShelf); }
  }, [deepShelf, shelfById]); // eslint-disable-line react-hooks/exhaustive-deps

  // put-mode: any in-stock can anywhere
  const { data: allCans = [] } = useQuery({
    queryKey: ["containers-list"], enabled: putMode,
    queryFn: async () => (await api.get("/containers")).data,
  });
  const { data: items = [] } = useQuery({
    queryKey: ["items-all"], enabled: putMode,
    queryFn: async () => (await api.get("/items", { params: { include_inactive: true } })).data,
  });
  const itemById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const pq = putQ.trim().toLowerCase();
  const putList = putMode ? allCans
    .map((c) => ({ ...c, item: itemById[c.item_id] }))
    .filter((c) => c.item && c.location_id !== selected)
    .filter((c) => !pq || [c.item.name, c.item.sku, c.item.supplier_name, ...(c.item.aliases || []), c.code, c.description]
      .filter(Boolean).some((s) => s.toLowerCase().includes(pq)))
    .sort((a, b) => (shelfById[a.location_id] ? 1 : 0) - (shelfById[b.location_id] ? 1 : 0) || a.item.name.localeCompare(b.item.name))
    .slice(0, 60) : [];

  const putHere = async (c) => {
    try {
      await api.post("/containers/move", { container_ids: [c.id], to_location_id: selected, note: "Placed via room map" });
      toast.success(`${c.item.name} → ${shelfById[selected]?.name}`);
      qc.invalidateQueries({ queryKey: ["containers-list"] });
      refetch();
    } catch (e) { toast.error(formatError(e)); }
  };

  const sel = selected ? shelfById[selected] : null;
  const selCans = selected ? cansByShelf[selected] || [] : [];

  return (
    <div className="space-y-4 animate-fade-in" data-testid="room-map">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="data-label">3D map</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Room Map</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Search an item and its shelf lights up. Drag to orbit, scroll/pinch to zoom, click any shelf to see or place cans.
          </p>
        </div>
        <select className="field-sm" value={roomId} onChange={(e) => { setRoomId(e.target.value); setSelected(null); setFocus(null); }}
                data-testid="map-room">
          {rooms.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.rack_count} racks</option>)}
        </select>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input className="field pl-9" placeholder="Find a can in this room… e.g. lavender, C2, musk"
               value={q} onChange={(e) => setQ(e.target.value)} data-testid="map-search" />
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-4">
        <div className="bg-white border border-border rounded-sm overflow-hidden h-[60vh] min-h-[380px]" data-testid="map-canvas">
          {roomId && data ? (
            <Canvas shadows camera={{ fov: 50, position: [3, 5.5, 11] }} dpr={[1, 1.5]}
                    onPointerMissed={() => { setSelected(null); setFocusCan(null); setPutMode(false); }}>
              <color attach="background" args={["#f4f1ea"]} />
              <ambientLight intensity={0.9} />
              <directionalLight position={[4, 8, 6]} intensity={1.1} castShadow />
              <Suspense fallback={null}>
                <Room layout={data.room.layout} />
                {racks.map((r) => (
                  <Rack key={r.id} rack={r} cans={cansByShelf} selectedShelf={selected}
                        matches={matches} hovered={hovered} onSelectShelf={showShelf} onHover={setHovered} />
                ))}
              </Suspense>
              <CameraRig focus={focus} room={data.room.layout} />
            </Canvas>
          ) : (
            <div className="h-full grid place-items-center text-sm text-muted-foreground p-6 text-center">
              No room has a 3D layout yet.
            </div>
          )}
        </div>

        <div className="space-y-3">
          {query && (
            <div className="bg-white border border-border rounded-sm" data-testid="map-results">
              <div className="px-3 py-2 border-b border-border data-label">{results.length} match{results.length === 1 ? "" : "es"}</div>
              <ul className="divide-y divide-border max-h-64 overflow-auto">
                {results.map((c) => {
                  const s = shelfById[c.location_id];
                  return (
                    <li key={c.id} className="p-2 text-sm flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{c.item_name} <span className="font-mono text-xs text-muted-foreground">{c.code.split("/").pop()}</span></div>
                        <div className="text-xs text-muted-foreground truncate">
                          <MapPin className="w-3 h-3 inline" /> {s ? s.name : "not on a shelf yet"} · {c.current_qty} {c.base_unit}
                        </div>
                      </div>
                      {s && <button className="btn-ghost text-xs" onClick={() => showShelf(s.id, c.id)}>Show</button>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="bg-white border border-border rounded-sm" data-testid="map-panel">
            {!sel ? (
              <div className="p-4 text-sm text-muted-foreground">
                Click a shelf in the model to see what's on it or to place cans there.
                {unplaced.length > 0 && (
                  <div className="mt-2 text-xs">
                    <b>{unplaced.length}</b> cans in this room aren't on a shelf yet — click a shelf, then “Put cans here”.
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="px-3 py-2 border-b border-border flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{sel.name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{sel.code}</div>
                  </div>
                  <button className="btn-ghost text-xs" onClick={() => { setSelected(null); setPutMode(false); }}><X className="w-4 h-4" /></button>
                </div>
                <ul className="divide-y divide-border max-h-56 overflow-auto">
                  {selCans.length === 0 && <li className="p-3 text-xs text-muted-foreground">Empty shelf.</li>}
                  {selCans.map((c) => (
                    <li key={c.id} className={`p-2 text-sm flex items-center gap-2 ${focusCan === c.id ? "bg-secondary/10" : ""}`}>
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ background: `hsl(${hueOf(c.item_id)},55%,60%)` }} />
                      <div className="flex-1 min-w-0 truncate">{c.item_name} <span className="font-mono text-xs text-muted-foreground">{c.code.split("/").pop()}</span></div>
                      <span className="font-mono text-xs">{c.current_qty} {c.base_unit}</span>
                    </li>
                  ))}
                </ul>
                <div className="p-2 border-t border-border">
                  {!putMode ? (
                    <button className="btn-primary text-xs w-full" onClick={() => setPutMode(true)} data-testid="map-put-mode">
                      <PackagePlus className="w-4 h-4 mr-1" /> Put cans here
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <input className="field-sm" placeholder="Search any can… (unplaced ones listed first)" autoFocus
                             value={putQ} onChange={(e) => setPutQ(e.target.value)} data-testid="map-put-search" />
                      <ul className="divide-y divide-border max-h-56 overflow-auto border border-border rounded-sm">
                        {putList.map((c) => (
                          <li key={c.id} className="p-2 text-xs flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{c.item.name} <span className="font-mono text-muted-foreground">{c.code.split("/").pop()}</span></div>
                              <div className="text-muted-foreground truncate">{c.current_qty} {c.item.base_unit} · {shelfById[c.location_id]?.name || "not on a shelf"}</div>
                            </div>
                            <button className="btn-primary text-xs" onClick={() => putHere(c)} data-testid={`map-put-${c.code}`}>Put here</button>
                          </li>
                        ))}
                        {putList.length === 0 && <li className="p-2 text-xs text-muted-foreground">No cans match.</li>}
                      </ul>
                      <button className="btn-ghost text-xs w-full" onClick={() => setPutMode(false)}>Done</button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
